import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, RefreshCw, MessageCircle, User, Mail, Phone, ChevronRight, X, Sparkles } from "lucide-react";

function fmt(dt) {
  if (!dt) return "—";
  try {
    const d = new Date(dt);
    return d.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  } catch (_) {
    return dt;
  }
}

export default function AdminWingmanPage() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [tLoading, setTLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/wingman/sessions");
      setSessions(data);
    } catch (e) {
      console.warn("AdminWingman: failed to load sessions", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openSession = async (sid) => {
    setActiveId(sid);
    setTranscript(null);
    setTLoading(true);
    try {
      const { data } = await api.get(`/admin/wingman/sessions/${sid}`);
      setTranscript(data);
    } catch (e) {
      console.warn("AdminWingman: failed to load transcript", e);
    } finally {
      setTLoading(false);
    }
  };

  const closeDrawer = () => { setActiveId(null); setTranscript(null); };

  const withContact = sessions.filter((s) => s.visitor_name || s.visitor_email || s.visitor_phone).length;

  return (
    <div className="p-8" data-testid="admin-wingman-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <span className="eyebrow">AI Sales Assistant</span>
          <h1 className="font-display text-3xl font-bold text-[#0d2040] mt-2">Akua Titan · Chats</h1>
          <p className="text-gray-500 text-sm mt-1">Every visitor conversation with Akua, plus captured leads.</p>
        </div>
        <button onClick={load} className="btn-outline-navy" data-testid="wingman-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4 mb-8" data-testid="wingman-stats">
        <StatCard label="Total chats" value={sessions.length} icon={MessageCircle} accent="bg-[#0d2040]" />
        <StatCard label="With contact info" value={withContact} icon={User} accent="bg-[#e63946]" />
        <StatCard
          label="Capture rate"
          value={sessions.length ? `${Math.round((withContact / sessions.length) * 100)}%` : "—"}
          icon={Sparkles}
          accent="bg-green-600"
        />
      </div>

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="inline-block animate-spin text-[#e63946]" /></div>
      ) : sessions.length === 0 ? (
        <div className="bg-white border border-gray-200 p-10 text-center text-gray-500" data-testid="wingman-empty">
          No chats yet. Akua Titan is live on every public page — once visitors say hi, sessions land here.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="wingman-sessions-table">
            <thead className="bg-[#0d2040] text-white">
              <tr>
                <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Visitor</th>
                <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Last message</th>
                <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Msgs</th>
                <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Started</th>
                <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Last active</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr
                  key={s.session_id}
                  onClick={() => openSession(s.session_id)}
                  className={`border-t border-gray-100 hover:bg-[#f7f8fa] cursor-pointer ${activeId === s.session_id ? "bg-[#f7f8fa]" : ""}`}
                  data-testid={`wingman-row-${s.session_id}`}
                >
                  <td className="p-3">
                    <div className="font-medium text-[#0d2040]">{s.visitor_name || <span className="text-gray-400">Anonymous</span>}</div>
                    {s.visitor_email && <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Mail size={11} /> {s.visitor_email}</div>}
                    {s.visitor_phone && <div className="text-xs text-gray-500 flex items-center gap-1"><Phone size={11} /> {s.visitor_phone}</div>}
                  </td>
                  <td className="p-3 max-w-md">
                    <div className="text-gray-700 truncate">
                      <span className={`text-[0.65rem] uppercase tracking-widest font-bold mr-2 ${s.last_role === "user" ? "text-[#e63946]" : "text-[#0d2040]"}`}>
                        {s.last_role === "user" ? "Visitor" : "Akua"}
                      </span>
                      {s.last_message}
                    </div>
                  </td>
                  <td className="p-3 font-display font-bold text-[#0d2040]">{s.messages}</td>
                  <td className="p-3 text-xs text-gray-500">{fmt(s.first_at)}</td>
                  <td className="p-3 text-xs text-gray-500">{fmt(s.last_at)}</td>
                  <td className="p-3 text-right text-gray-400"><ChevronRight size={16} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Transcript drawer */}
      {activeId && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={closeDrawer} data-testid="wingman-drawer-backdrop" />
          <aside
            className="fixed right-0 top-0 bottom-0 w-full max-w-[520px] bg-white border-l border-gray-200 z-50 flex flex-col"
            data-testid="wingman-drawer"
          >
            <div className="bg-[#0d2040] text-white p-5 flex items-start justify-between">
              <div>
                <div className="text-[0.65rem] uppercase tracking-widest text-white/60">Transcript</div>
                <div className="font-display text-xl font-bold mt-1">{transcript?.visitor?.name || "Anonymous visitor"}</div>
                <div className="text-xs text-white/70 mt-1 space-y-0.5">
                  {transcript?.visitor?.email && <div>{transcript.visitor.email}</div>}
                  {transcript?.visitor?.phone && <div>{transcript.visitor.phone}</div>}
                  <div className="text-[0.65rem] text-white/40 mt-2 font-mono">{activeId}</div>
                </div>
              </div>
              <button onClick={closeDrawer} className="p-1.5 hover:bg-white/10" data-testid="wingman-drawer-close" aria-label="Close">
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-[#f7f8fa]">
              {tLoading && <div className="text-center py-10"><Loader2 className="inline-block animate-spin text-[#e63946]" /></div>}
              {!tLoading && transcript?.messages?.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[88%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user" ? "bg-[#0d2040] text-white" : "bg-white border border-gray-200 text-[#0d2040]"
                  }`}>
                    <div className={`text-[0.6rem] uppercase tracking-widest mb-1 ${m.role === "user" ? "text-white/50" : "text-[#e63946]"}`}>
                      {m.role === "user" ? "Visitor" : "Akua"} · {fmt(m.created_at)}
                    </div>
                    {m.content}
                  </div>
                </div>
              ))}
              {!tLoading && (!transcript?.messages || transcript.messages.length === 0) && (
                <div className="text-center text-gray-500 text-sm py-10">No messages yet.</div>
              )}
            </div>
          </aside>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-gray-200 p-5" data-testid={`wingman-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-gray-500">{label}</div>
        <div className={`w-8 h-8 ${accent} flex items-center justify-center`}>
          <Icon size={14} className="text-white" />
        </div>
      </div>
      <div className="font-display text-3xl font-bold text-[#0d2040] mt-3">{value}</div>
    </div>
  );
}
